'use client'

import * as React from 'react'

// Real review-source logos by domain. We resolve a logo through a fallback
// chain so a tile is never broken:
//   1. Brandfetch CDN — only when NEXT_PUBLIC_BRANDFETCH_ID is set, because
//      the CDN returns a (non-erroring) placeholder without a client id,
//      which silently leaves the tile broken.
//   2. Google's favicon service — loads in the visitor's browser and returns
//      the real brand mark for every one of these well-known domains
//      (Feefo, Loox, Okendo, Judge.me, Reviews.io, Bazaarvoice…), which
//      SimpleIcons does not cover.
//   3. SimpleIcons — clean monochrome marks for the brands it does have.
//   4. Brand-coloured monogram — guaranteed final fallback.
const DOMAINS: Record<string, string> = {
  google: 'google.com', trustpilot: 'trustpilot.com', feefo: 'feefo.com',
  yotpo: 'yotpo.com', loox: 'loox.io', judgeme: 'judge.me', okendo: 'okendo.co',
  reviewsio: 'reviews.io', bazaarvoice: 'bazaarvoice.com', facebook: 'facebook.com',
  tripadvisor: 'tripadvisor.com', amazon: 'amazon.com',
}

const BRANDFETCH_ID = process.env.NEXT_PUBLIC_BRANDFETCH_ID

export function SourceLogo({ slug, color, name }: { slug: string; color: string; name: string }) {
  const domain = DOMAINS[slug]
  const chain = React.useMemo(
    () =>
      [
        BRANDFETCH_ID && domain ? `https://cdn.brandfetch.io/${domain}/w/64/h/64/logo?c=${BRANDFETCH_ID}` : '',
        domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64` : '',
        `https://cdn.simpleicons.org/${slug}/${color}`,
      ].filter(Boolean),
    [domain, slug, color],
  )
  const [step, setStep] = React.useState(0)

  if (step >= chain.length) {
    return (
      <span
        className="grid h-7 w-7 place-items-center rounded-md text-sm font-bold"
        style={{ color: `#${color}`, background: `#${color}1a` }}
        aria-label={`${name} logo`}
      >
        {name.charAt(0).toUpperCase()}
      </span>
    )
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={chain[step]}
      alt={`${name} logo`}
      width={28}
      height={28}
      loading="lazy"
      onError={() => setStep((s) => s + 1)}
      className="h-7 w-7 object-contain"
    />
  )
}
